"use client"

import Link from 'next/link'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@open-mercato/ui/primitives/button'
import { DriverErrorState, DriverLoadingState, DriverPanel, DriverPwaShell, DriverStatusBadge, fetchDriverOrders, fetchDriverProfile, formatDriverDateTime } from '../../../lib/driverPwaUi'

export default function DriverOrdersPage() {
  const profileQuery = useQuery({
    queryKey: ['driver-pwa', 'profile'],
    queryFn: fetchDriverProfile,
    retry: false,
  })
  const ordersQuery = useQuery({
    queryKey: ['driver-pwa', 'orders'],
    queryFn: fetchDriverOrders,
    retry: false,
    refetchInterval: 30000,
  })

  if ((profileQuery.isLoading && !profileQuery.data) || (ordersQuery.isLoading && !ordersQuery.data)) {
    return <DriverLoadingState />
  }

  if (profileQuery.isError) {
    return <DriverErrorState title="Driver PWA is not available" description={profileQuery.error instanceof Error ? profileQuery.error.message : 'This account is not linked to an active driver profile.'} />
  }

  if (ordersQuery.isError || !ordersQuery.data) {
    return <DriverErrorState title="Orders are unavailable" description={ordersQuery.error instanceof Error ? ordersQuery.error.message : 'Failed to load assigned work.'} />
  }

  const activeOrder = ordersQuery.data.items.find((item) => item.id === ordersQuery.data.activeDispatchId) ?? null

  return (
    <DriverPwaShell
      title="My Orders"
      subtitle="Assigned work appears here. The list refreshes automatically so dispatch changes show up inside the normal operating window."
      profile={profileQuery.data ?? null}
    >
      {activeOrder ? (
        <DriverPanel
          eyebrow="Active now"
          title={activeOrder.orderNumber}
          action={(
            <Button asChild className="rounded-2xl bg-amber-400 text-slate-950 hover:bg-amber-300">
              <Link href="/driver/active">Open</Link>
            </Button>
          )}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-slate-300">
                {activeOrder.originLabel ?? 'Origin pending'} to {activeOrder.destinationLabel ?? 'Destination pending'}
              </div>
              <DriverStatusBadge status={activeOrder.status} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-2xl bg-white/6 px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Pickup</div>
                <div className="mt-2 font-medium">{formatDriverDateTime(activeOrder.plannedPickupAt)}</div>
              </div>
              <div className="rounded-2xl bg-white/6 px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Delivery</div>
                <div className="mt-2 font-medium">{formatDriverDateTime(activeOrder.plannedDeliveryAt)}</div>
              </div>
            </div>
          </div>
        </DriverPanel>
      ) : null}

      <DriverPanel eyebrow="Assignments" title="Order Queue">
        <div className="space-y-3">
          {ordersQuery.data.items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/12 px-4 py-6 text-sm leading-6 text-slate-300">
              No dispatch assignments are currently linked to this driver.
            </div>
          ) : null}

          {ordersQuery.data.items.map((order) => {
            const isActive = order.id === ordersQuery.data.activeDispatchId
            return (
              <article key={order.id} className={`rounded-[24px] border px-4 py-4 ${isActive ? 'border-sky-400/25 bg-sky-500/10' : 'border-white/8 bg-white/5'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold">{order.orderNumber}</div>
                    <div className="mt-1 text-sm text-slate-300">
                      {order.originLabel ?? 'Origin pending'} to {order.destinationLabel ?? 'Destination pending'}
                    </div>
                  </div>
                  <DriverStatusBadge status={order.status} />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-slate-200">
                  <div className="rounded-2xl bg-slate-950/45 px-3 py-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Pickup</div>
                    <div className="mt-2">{formatDriverDateTime(order.plannedPickupAt)}</div>
                  </div>
                  <div className="rounded-2xl bg-slate-950/45 px-3 py-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Delivery</div>
                    <div className="mt-2">{formatDriverDateTime(order.plannedDeliveryAt)}</div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3 text-sm text-slate-300">
                  <div>Vehicle: {order.vehiclePlate ?? 'Not assigned'}</div>
                  <div>Photos: {order.attachmentCount}</div>
                </div>

                {isActive ? (
                  <div className="mt-4">
                    <Button asChild className="w-full rounded-2xl bg-sky-500 text-slate-950 hover:bg-sky-400">
                      <Link href="/driver/active">Continue active order</Link>
                    </Button>
                  </div>
                ) : null}
              </article>
            )
          })}
        </div>
      </DriverPanel>
    </DriverPwaShell>
  )
}
